import type { BotPersonality } from "./world.js";
import { HQ_MILESTONES } from "./progression.js";

/** Chat, mail, profiles, leaderboards and neighbours (80-social.md R71–R76). */

export const CHAT_RATE_LIMIT_MS = 2000;
export const CHAT_HISTORY = 200;
/** How long a channel keeps its history (D67); DMs are kept. */
export const CHAT_RETENTION_HOURS = { global: 6, system: 24, alliance: 24 } as const;
/** Retention window of a channel in milliseconds, or null when the channel keeps everything (DMs). */
export function chatRetentionMs(channel: string): number | null {
  if (channel === "global") return CHAT_RETENTION_HOURS.global * 3_600_000;
  if (channel === "system") return CHAT_RETENTION_HOURS.system * 3_600_000;
  if (channel.startsWith("ally:")) return CHAT_RETENTION_HOURS.alliance * 3_600_000;
  return null;
}
export const CHAT_MAX_LENGTH_CAP = 300;
export const MAIL_TRASH_DAYS = 7;
export const MAIL_CAPACITY_BASE = 200;
export const NEIGHBOUR_RADIUS = 15;
export const ASSIST_SECONDS = 60;
export const ASSIST_MAX = 10;
export const DELETE_GRACE_DAYS = 7;
export const CONTACT_LIMIT = 100;
export const BLOCK_LIMIT = 100;
export const LEADERBOARD_TOP = 100;
export const STATS_TTL_MS = 5 * 60_000;

export type ChatChannelKind = "global" | "system" | "dm";

export interface EmoteDef {
  id: string;
  glyph: string;
  /** Comms Tower level that unlocks the emote (1 = always). */
  unlock: number;
  /** Store pack required in the active emote slot; independent of Comms level. */
  pack?: string;
}

/** `:id:` in a message renders as the glyph; higher Comms Tower levels unlock more (R71). */
export const EMOTES: readonly EmoteDef[] = [
  { id: "zombie", glyph: "🧟", unlock: 1 },
  { id: "skull", glyph: "💀", unlock: 1 },
  { id: "fire", glyph: "🔥", unlock: 1 },
  { id: "gg", glyph: "🤝", unlock: 1 },
  { id: "salute", glyph: "🫡", unlock: 1 },
  { id: "laugh", glyph: "😂", unlock: 1 },
  { id: "shield", glyph: "🛡️", unlock: 3 },
  { id: "bolt", glyph: "⚡", unlock: 3 },
  { id: "rocket", glyph: "🚀", unlock: 5 },
  { id: "gem", glyph: "💎", unlock: 5 },
  { id: "crown", glyph: "👑", unlock: 8 },
  { id: "eyes", glyph: "👀", unlock: 8 },
  { id: "boom", glyph: "💥", unlock: 10 },
  { id: "target", glyph: "🎯", unlock: 10 },
  { id: "ghost", glyph: "👻", unlock: 12 },
  { id: "robot", glyph: "🤖", unlock: 12 },
  { id: "trophy", glyph: "🏆", unlock: 15 },
  { id: "alien", glyph: "👽", unlock: 15 },
  { id: "dragon", glyph: "🐉", unlock: 20 },
  { id: "nuke", glyph: "☢️", unlock: 20 },
  { id: "sprout", glyph: ":sprout:", unlock: 1, pack: "cos.emotes.wasteland" },
  { id: "wrench", glyph: ":wrench:", unlock: 1, pack: "cos.emotes.wasteland" },
  { id: "campfire", glyph: ":campfire:", unlock: 1, pack: "cos.emotes.wasteland" },
  { id: "heartbot", glyph: ":heartbot:", unlock: 1, pack: "cos.emotes.neon" },
  { id: "signal", glyph: ":signal:", unlock: 1, pack: "cos.emotes.neon" },
  { id: "starburst", glyph: ":starburst:", unlock: 1, pack: "cos.emotes.neon" },
];

export interface ChatColourDef {
  id: string;
  hex: string;
  unlock: number;
}

/** Name colours in chat, unlocked by the Comms Tower (R71). */
export const CHAT_COLOURS: readonly ChatColourDef[] = [
  { id: "default", hex: "#e6edf3", unlock: 1 },
  { id: "cyan", hex: "#22d3ee", unlock: 5 },
  { id: "lime", hex: "#a3e635", unlock: 8 },
  { id: "amber", hex: "#fbbf24", unlock: 10 },
  { id: "rose", hex: "#fb7185", unlock: 12 },
  { id: "violet", hex: "#a78bfa", unlock: 15 },
  { id: "gold", hex: "#f5c542", unlock: 20 },
];

export type TitleCondition =
  | { kind: "always" }
  | { kind: "hq"; level: number }
  | { kind: "kills"; count: number }
  | { kind: "raids"; count: number }
  | { kind: "defences"; count: number }
  | { kind: "collection"; count: number }
  | { kind: "arena"; rating: number }
  | { kind: "vip"; level: number }
  | { kind: "spent"; credits: number };

export interface TitleDef {
  id: string;
  name: { de: string; en: string };
  condition: TitleCondition;
}

/** Profile titles by achievement (R73). */
export const TITLES: readonly TitleDef[] = [
  { id: "survivor", name: { de: "Überlebende:r", en: "Survivor" }, condition: { kind: "always" } },
  ...HQ_MILESTONES.map((m) => ({ id: m.titleId, name: m.title, condition: { kind: "hq" as const, level: m.level } })),
  { id: "hunter", name: { de: "Jäger:in", en: "Hunter" }, condition: { kind: "kills", count: 50 } },
  { id: "exterminator", name: { de: "Vernichter:in", en: "Exterminator" }, condition: { kind: "kills", count: 500 } },
  { id: "raider", name: { de: "Plünderer:in", en: "Raider" }, condition: { kind: "raids", count: 10 } },
  { id: "warlord", name: { de: "Kriegsherr:in", en: "Warlord" }, condition: { kind: "raids", count: 100 } },
  { id: "bastion", name: { de: "Bastion", en: "Bastion" }, condition: { kind: "defences", count: 25 } },
  { id: "collector", name: { de: "Sammler:in", en: "Collector" }, condition: { kind: "collection", count: 30 } },
  { id: "gladiator", name: { de: "Gladiator:in", en: "Gladiator" }, condition: { kind: "arena", rating: 1400 } },
  { id: "champion", name: { de: "Champion", en: "Champion" }, condition: { kind: "arena", rating: 1800 } },
  { id: "patron", name: { de: "Mäzen", en: "Patron" }, condition: { kind: "vip", level: 5 } },
  { id: "whale", name: { de: "Wal", en: "Whale" }, condition: { kind: "spent", credits: 10_000 } },
];

export interface FrameDef {
  id: string;
  name: { de: string; en: string };
  /** VIP level that unlocks the frame (0 = everyone). */
  vip: number;
  colour: string;
}

/** Portrait frames by VIP level (R73). */
export const FRAMES: readonly FrameDef[] = [
  { id: "steel", name: { de: "Stahl", en: "Steel" }, vip: 0, colour: "#8b93a1" },
  { id: "bronze", name: { de: "Bronze", en: "Bronze" }, vip: 2, colour: "#b45309" },
  { id: "silver", name: { de: "Silber", en: "Silver" }, vip: 5, colour: "#cbd5e1" },
  { id: "gold", name: { de: "Gold", en: "Gold" }, vip: 8, colour: "#f5c542" },
  { id: "neon", name: { de: "Neon", en: "Neon" }, vip: 12, colour: "#22d3ee" },
  { id: "void", name: { de: "Leere", en: "Void" }, vip: 16, colour: "#a78bfa" },
  { id: "mega", name: { de: "MegaVIP", en: "MegaVIP" }, vip: 20, colour: "#fb7185" },
];

/** Default profanity list; admins edit the live list (R71). Kept short and obviously non-exhaustive. */
export const PROFANITY_DEFAULT: readonly string[] = ["arschloch", "wichser", "hurensohn", "fotze", "asshole", "bastard", "bitch", "cunt", "fuck", "motherfucker", "nigger", "retard", "shit", "wanker"];

export type LeaderboardId = "power" | "hq" | "kills" | "arena" | "spent" | "collection";

export interface LeaderboardDef {
  id: LeaderboardId;
  name: { de: string; en: string };
  unit: { de: string; en: string };
}

export const LEADERBOARDS: readonly LeaderboardDef[] = [
  { id: "power", name: { de: "Stärke", en: "Power" }, unit: { de: "Stärke", en: "power" } },
  { id: "hq", name: { de: "HQ-Stufe", en: "HQ level" }, unit: { de: "HQ", en: "HQ" } },
  { id: "kills", name: { de: "Abschüsse", en: "Kills" }, unit: { de: "Siege", en: "wins" } },
  { id: "arena", name: { de: "Arena", en: "Arena" }, unit: { de: "Wertung", en: "rating" } },
  { id: "spent", name: { de: "Top-Spender", en: "Top Spenders" }, unit: { de: "₵", en: "₵" } },
  { id: "collection", name: { de: "Sammlung", en: "Collection" }, unit: { de: "Einheiten", en: "units" } },
];

/** Templated bot lines per personality; `{name}` and `{target}` are filled by the runner (R71). */
export const BOT_CHAT_LINES: Record<BotPersonality, readonly string[]> = {
  turtle: ["Mauer steht. Wer klopft, bleibt draußen. :shield:", "Ruhige Nacht im Ascheland.", "Schild läuft, Tee kocht.", "Wall holds. Knock and stay out. :shield:"],
  farmer: ["Depot L2 bei mir in der Nähe, wer will? :gg:", "Rationen für alle, Polymer für niemanden.", "Sammeln, bauen, schlafen. Repeat.", "Gathering again. Nodes near {name} are rich."],
  raider: ["Wer kein Schild hat, hat bald keine Rationen. :fire:", "{target}, nette Lagerhalle. Wär schade drum.", "Schilde sind für Feiglinge.", "No shield, no mercy. :skull:"],
  opportunist: ["Ich beobachte nur. :eyes:", "Wer mich angreift, kriegt Besuch.", "Interessante Zeiten.", "Just watching. For now. :eyes:"],
  warlord: ["Ground Zero gehört mir. :crown:", "Hive bei 60 %, wer hilft mit? :zombie:", "{target} hat HQ {hq}. Reicht nicht.", "Boss window in one hour. Be there."],
  newbie: ["Wie sammle ich Legierung?", "Hat jemand Tipps für den Querschnitt?", "Mein erstes Rudel besiegt! :gg:", "How do I raise the HQ average?"],
};

export const BOT_CHAT_MIN_MINUTES = 30;
export const BOT_CHAT_MAX_MINUTES = 90;

export const LAST_SEEN_HEARTBEAT_MINUTES = 5;
